var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/environment';
import ARCHIVE_PRODUCT from 'api/mutations/ARCHIVE_PRODUCT';
import REMOVE_PRODUCT from 'api/mutations/REMOVE_PRODUCT';
import RETRIEVE_PRODUCT from 'api/mutations/RETRIEVE_PRODUCT';
import AUTOCOMPLETE_PRODUCT_CATEGORIES from 'api/queries/AUTOCOMPLETE_PRODUCT_CATEGORIES';
import GET_CURRENT_TENANT_SUPPLIERS from 'api/queries/GET_CURRENT_TENANT_SUPPLIERS';
import GET_TENANT_PRODUCTS from 'api/queries/GET_TENANT_PRODUCTS';
import GET_UNASSIGNED_PRODUCTS from 'api/queries/GET_UNASSIGNED_PRODUCTS';
import parseCategories from 'parsers/specification/parseCategories';
const initialState = {
    selectedComponent: {
        item: null,
        status: 'idle',
        error: null,
    },
    items: [],
    sharedItems: [],
    status: {
        fetch: 'idle',
        fetchShared: 'idle',
        add: 'idle',
        delete: 'idle',
        other: 'idle',
    },
    errors: {
        fetch: null,
        fetchShared: null,
        add: null,
        delete: null,
        other: null,
    },
    refetchFlags: {
        fetch: false,
        fetchShared: false,
        add: false,
        delete: false,
        other: false,
    },
    pageInfo: {
        hasNextPage: true,
        endCursor: null,
    },
    categories: {
        items: [],
        status: 'idle',
        error: null,
    },
    suppliers: {
        items: [],
        status: 'idle',
        error: null,
    },
    filters: {
        name: null,
        categoryId: null,
        supplierId: null,
        statusId: '',
        functionIds: [],
        sustainabilityIds: [],
        certificateIds: [],
        continentIds: [],
        countryIds: [],
        eventIds: [],
        mainCategoryIds: [],
    },
};
const areFilterValuesEqual = (currentValue, nextValue) => {
    if (Array.isArray(currentValue) && Array.isArray(nextValue)) {
        return (currentValue.length === nextValue.length &&
            currentValue.every((value, index) => value === nextValue[index]));
    }
    return currentValue === nextValue;
};
const productParameterFilterVariables = (filters, categories) => {
    const category = categories.find((item) => item.id === filters.categoryId);
    const filtersAvailable = Boolean(category === null || category === void 0 ? void 0 : category.filtersAvailable);
    return {
        functionIds: filtersAvailable && filters.functionIds.length ? filters.functionIds : null,
        sustainabilityIds: filtersAvailable && filters.sustainabilityIds.length ? filters.sustainabilityIds : null,
        certificateIds: filtersAvailable && filters.certificateIds.length ? filters.certificateIds : null,
        continentIds: filtersAvailable && filters.continentIds.length ? filters.continentIds : null,
        countryIds: filtersAvailable && filters.countryIds.length ? filters.countryIds : null,
        eventIds: filtersAvailable && filters.eventIds.length ? filters.eventIds : null,
        mainCategoryIds: filtersAvailable && filters.mainCategoryIds.length ? filters.mainCategoryIds : null,
    };
};
export const fetchComponents = createAsyncThunk('components/fetchComponents', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { getState, rejectWithValue }) {
    const state = getState();
    const { filters, pageInfo } = state.components;
    const parameterFilters = productParameterFilterVariables(filters, state.components.categories.items);
    const categoriesStatus = state.components.categories.status;
    if (categoriesStatus === 'succeeded') {
        try {
            const response = yield client.query({
                query: GET_TENANT_PRODUCTS,
                variables: Object.assign(Object.assign({ name: filters.name || null, categoryId: filters.categoryId || null, supplierId: filters.supplierId || null, statusId: filters.statusId || null }, parameterFilters), { first: 20, after: pageInfo.endCursor }),
                fetchPolicy: 'network-only',
                // without this fetchPolicy - no queries are send after the first one
            });
            return response.data.me.currentTenant.products;
        }
        catch (error) {
            const apolloError = error;
            return rejectWithValue(apolloError.message);
        }
    }
    return rejectWithValue('Categories not loaded');
}));
export const fetchSharedComponents = createAsyncThunk('components/fetchSharedComponents', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { getState, rejectWithValue }) {
    const state = getState();
    const { filters, pageInfo } = state.components;
    const parameterFilters = productParameterFilterVariables(filters, state.components.categories.items);
    try {
        const response = yield client.query({
            query: GET_UNASSIGNED_PRODUCTS,
            variables: Object.assign(Object.assign({ name: filters.name || null, categoryId: filters.categoryId || null, supplierId: filters.supplierId || null, statusId: filters.statusId || null }, parameterFilters), { first: 20, after: pageInfo.endCursor }),
            fetchPolicy: 'network-only',
            // without this fetchPolicy - no queries are send after the first one
            // TODO: Check how caching queries works
        });
        return response.data.autocomplete.unassignedProducts;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchCategories = createAsyncThunk('components/fetchCategories', () => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield client.query({
        query: AUTOCOMPLETE_PRODUCT_CATEGORIES,
        fetchPolicy: 'network-only',
    });
    return parseCategories(response.data);
}));
export const fetchSuppliers = createAsyncThunk('components/fetchSuppliers', (args_1, _a) => __awaiter(void 0, [args_1, _a], void 0, function* (args, { rejectWithValue }) {
    try {
        const response = yield client.query({
            query: GET_CURRENT_TENANT_SUPPLIERS,
            variables: args,
        });
        return response.data.me.currentTenant.suppliers.edges.map((edge) => edge.node);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const deleteComponent = createAsyncThunk('components/deleteComponent', (componentId_1, _a) => __awaiter(void 0, [componentId_1, _a], void 0, function* (componentId, { rejectWithValue }) {
    try {
        const response = yield client.mutate({
            mutation: REMOVE_PRODUCT,
            variables: { productId: componentId },
        });
        if (response.data.removeProduct.removed) {
            return componentId;
        }
        return rejectWithValue(response.data.removeProduct.message);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const changeComponentStatus = createAsyncThunk('components/changeComponentStatus', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ componentId, status }, { rejectWithValue }) {
    var _c;
    try {
        const archive = status === 'ARCHIVED';
        const response = yield client.mutate({
            mutation: archive ? ARCHIVE_PRODUCT : RETRIEVE_PRODUCT,
            variables: { productId: componentId },
        });
        const product = (_c = (archive ? response.data.archiveProduct : response.data.retrieveProduct)) === null || _c === void 0 ? void 0 : _c.product;
        if (!product) {
            return rejectWithValue('Component not found');
        }
        return { id: product.id, status: product.status };
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const componentsSlice = createSlice({
    name: 'components',
    initialState,
    reducers: {
        setFilter(state, action) {
            const { filterName, value } = action.payload;
            if (areFilterValuesEqual(state.filters[filterName], value)) {
                return;
            }
            state.filters[filterName] = value;
        },
        resetFilters(state) {
            state.filters = {
                name: null,
                categoryId: null,
                supplierId: null,
                statusId: null,
                functionIds: [],
                sustainabilityIds: [],
                certificateIds: [],
                continentIds: [],
                countryIds: [],
                eventIds: [],
                mainCategoryIds: [],
            };
        },
        resetCategories(state) {
            state.categories = {
                items: [],
                status: 'idle',
                error: null,
            };
        },
        resetComponents(state) {
            state.items = [];
            state.pageInfo = { hasNextPage: true, endCursor: null };
            state.status = {
                fetch: 'idle',
                fetchShared: 'idle',
                add: 'idle',
                delete: 'idle',
                other: 'idle',
            };
            state.errors = {
                fetch: null,
                fetchShared: null,
                add: null,
                delete: null,
                other: null,
            };
            state.refetchFlags = {
                fetch: false,
                fetchShared: false,
                add: false,
                delete: false,
                other: false,
            };
        },
        resetSharedComponents(state) {
            state.sharedItems = [];
            state.pageInfo = { hasNextPage: true, endCursor: null };
        },
        addItem: (state, action) => {
            const { data } = action.payload;
            state.items = [data, ...state.items];
        },
        updateItemById: (state, action) => {
            const { id, data } = action.payload;
            const itemIndex = state.items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                state.items[itemIndex] = Object.assign(Object.assign({}, state.items[itemIndex]), data);
            }
        },
        increaseVariantsCounter: (state, action) => {
            const { id } = action.payload;
            const itemIndex = state.items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                state.items[itemIndex] = Object.assign(Object.assign({}, state.items[itemIndex]), { productVariantsCount: state.items[itemIndex].productVariantsCount + 1, activeProductVariantsCount: state.items[itemIndex].activeProductVariantsCount + 1 });
            }
        },
        decreaseVariantsCounter: (state, action) => {
            const { id } = action.payload;
            const itemIndex = state.items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                state.items[itemIndex] = Object.assign(Object.assign({}, state.items[itemIndex]), { productVariantsCount: state.items[itemIndex].productVariantsCount - 1, activeProductVariantsCount: state.items[itemIndex].activeProductVariantsCount - 1 });
            }
        },
        increaseCategoryCounter: (state, action) => {
            const { id } = action.payload;
            const itemIndex = state.categories.items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                state.categories.items[itemIndex] = Object.assign(Object.assign({}, state.categories.items[itemIndex]), { productsCount: state.categories.items[itemIndex].productsCount + 1 });
            }
        },
        decreaseCategoryCounter: (state, action) => {
            const { id } = action.payload;
            const itemIndex = state.categories.items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                state.categories.items[itemIndex] = Object.assign(Object.assign({}, state.categories.items[itemIndex]), { productsCount: state.categories.items[itemIndex].productsCount - 1 });
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchComponents.pending, (state) => {
            state.status.fetch = 'loading';
        })
            .addCase(fetchComponents.fulfilled, (state, action) => {
            var _a, _b, _c;
            const newComponents = ((_b = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.edges) === null || _b === void 0 ? void 0 : _b.map((edge) => edge.node)) || [];
            const uniqueComponents = newComponents.filter((newComponent) => !state.items.some((existingComponent) => existingComponent.id === newComponent.id));
            state.items = [...state.items, ...uniqueComponents];
            state.pageInfo = (_c = action.payload) === null || _c === void 0 ? void 0 : _c.pageInfo;
            state.status.fetch = 'succeeded';
        })
            .addCase(fetchComponents.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch = action.error.message || 'Error fetching components';
        })
            .addCase(fetchSharedComponents.pending, (state) => {
            state.status.fetchShared = 'loading';
        })
            .addCase(fetchSharedComponents.fulfilled, (state, action) => {
            var _a;
            const newComponents = action.payload.edges.map((edge) => edge.node);
            const uniqueComponents = newComponents.filter((newComponent) => !state.sharedItems.some((existingComponent) => existingComponent.id === newComponent.id));
            state.sharedItems = [...state.sharedItems, ...uniqueComponents];
            state.pageInfo = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.pageInfo;
            state.status.fetchShared = 'succeeded';
        })
            .addCase(fetchSharedComponents.rejected, (state, action) => {
            state.status.fetchShared = 'failed';
            state.errors.fetchShared = action.error.message || 'Error fetching shared components';
        })
            .addCase(fetchCategories.pending, (state) => {
            state.categories.status = 'loading';
        })
            .addCase(fetchCategories.fulfilled, (state, action) => {
            state.categories.items = action.payload;
            state.categories.status = 'succeeded';
            state.refetchFlags.fetchCategories = false;
        })
            .addCase(fetchCategories.rejected, (state, action) => {
            state.categories.status = 'failed';
            state.categories.error = action.error.message || 'Failed to fetch categories';
            state.refetchFlags.fetchCategories = false;
        })
            .addCase(fetchSuppliers.pending, (state) => {
            state.suppliers.status = 'loading';
        })
            .addCase(fetchSuppliers.fulfilled, (state, action) => {
            state.suppliers.items = action.payload;
            state.suppliers.status = 'succeeded';
        })
            .addCase(fetchSuppliers.rejected, (state, action) => {
            state.suppliers.status = 'failed';
            state.suppliers.error = action.error.message || 'Failed to fetch categories';
        })
            .addCase(deleteComponent.pending, (state) => {
            // state.items = state.items.filter((component) => component.id !== action.payload);
            state.status.delete = 'loading';
        })
            .addCase(deleteComponent.fulfilled, (state, action) => {
            state.items = state.items.filter((component) => component.id !== action.payload);
            state.status.delete = 'succeeded';
        })
            .addCase(deleteComponent.rejected, (state, action) => {
            state.status.delete = 'failed';
            state.errors.delete = action.payload;
        })
            .addCase(changeComponentStatus.fulfilled, (state, action) => {
            const { id, status } = action.payload;
            const statusFilter = state.filters.statusId;
            if (statusFilter && statusFilter !== status) {
                state.items = state.items.filter((component) => component.id !== id);
                return;
            }
            const itemIndex = state.items.findIndex((component) => component.id === id);
            if (itemIndex !== -1) {
                state.items[itemIndex] = Object.assign(Object.assign({}, state.items[itemIndex]), { status });
            }
        });
    },
});
export const { setFilter, resetFilters, resetCategories, resetComponents, resetSharedComponents, addItem, updateItemById, increaseVariantsCounter, decreaseVariantsCounter, increaseCategoryCounter, decreaseCategoryCounter, } = componentsSlice.actions;
export const selectCategoryById = (state, categoryId) => state.components.categories.items.find((category) => category.id === categoryId);
export const selectComponentsStatus = (state) => state.components.status;
export const selectComponentsErrors = (state) => state.components.errors;
export const selectComponentsRefetchFlags = (state) => state.components.refetchFlags;
export default componentsSlice.reducer;
